import React, { useEffect, useMemo, useCallback } from "react";
import Script from "next/script";
import { META_PIXEL_ID } from "~/lib/meta-tracking";
import {
  COOKIE_CONSENT_STORAGE_KEY,
  type ConsentState,
  consentStateFromDefault,
  getBrowserCookieValue,
  getBrowserConsentCountryCode,
  getConsentDefault,
  parseStoredConsent,
  persistConsent,
} from "~/lib/consent-region";
import { markGA4Configured } from "~/lib/ga4-browser";
import { flushPendingGA4PurchaseEvents } from "~/lib/ga4-purchase";

const GA_MEASUREMENT_ID = "G-QM5VJMMHBZ";
const GOOGLE_ADS_ID = "AW-16745275047";

const getStoredConsent = (initialConsent: ConsentState): ConsentState => {
  if (typeof window === "undefined") {
    return initialConsent;
  }

  return (
    parseStoredConsent(localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)) ??
    parseStoredConsent(getBrowserCookieValue(COOKIE_CONSENT_STORAGE_KEY)) ??
    consentStateFromDefault(getConsentDefault(getBrowserConsentCountryCode())) ??
    initialConsent
  );
};

export default function ConsentBasedScripts({
  initialConsent,
}: {
  initialConsent: ConsentState;
}) {
  const [storageKey, setStorageKey] = React.useState(0);

  const consent = useMemo(
    () => getStoredConsent(initialConsent),
    [storageKey, initialConsent],
  );

  const consentDefault =
    initialConsent.adConsent && initialConsent.analyticsConsent
      ? "granted"
      : "denied";

  // For default-granted regions: persist the implicit allow locally so
  // browser-side event helpers that read localStorage stay consistent.
  useEffect(() => {
    const grantedDefault = consentStateFromDefault("granted");
    if (consent.adConsent !== grantedDefault.adConsent ||
      consent.analyticsConsent !== grantedDefault.analyticsConsent) {
      return;
    }
    if (localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)) {
      return;
    }
    persistConsent(consent, { notify: false });
  }, [consent]);

  // Push consent update to gtag when preferences change, then fire gtag('config')
  // so the initial page_view always fires AFTER consent is established.
  // The inline script no longer calls gtag('config') — it only sets defaults + 'js'.
  // storageKey starts at 0 (initial mount). On first render:
  //   - If consent is all denied: skip the redundant update, but still fire config
  //     so that the page_view is sent (as a cookieless ping, per Consent Mode).
  //   - If the user has stored consent: update first, then config → page_view with
  //     analytics_storage granted, so it appears in GA4 realtime and reports.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      return;
    }
    // Only skip the consent update when nothing is granted (avoids duplicate denied).
    // Still fall through to fire config so the page_view is always sent.
    if (
      !(storageKey === 0 && !consent.adConsent && !consent.analyticsConsent)
    ) {
      window.gtag("consent", "update", {
        ad_storage: consent.adConsent ? "granted" : "denied",
        ad_user_data: consent.adConsent ? "granted" : "denied",
        ad_personalization: consent.adConsent ? "granted" : "denied",
        analytics_storage: consent.analyticsConsent ? "granted" : "denied",
      });
    }
    // Fire config (page_view) on initial mount only, after consent is resolved.
    // On subsequent storageKey increments (user just accepted consent mid-session),
    // fire a manual page_view so the session becomes visible in GA4 Realtime
    // without requiring the user to navigate to another page.
    if (storageKey === 0) {
      window.gtag("config", GA_MEASUREMENT_ID);
      window.gtag("config", GOOGLE_ADS_ID);
      markGA4Configured();
      flushPendingGA4PurchaseEvents();
    } else if (storageKey > 0) {
      if (consent.analyticsConsent) {
        window.gtag("event", "page_view", {
          page_location: window.location.href,
          page_title: document.title,
        });
      }
      flushPendingGA4PurchaseEvents();
    }
  }, [consent.adConsent, consent.analyticsConsent, storageKey]);

  const handleStorageChange = useCallback(() => {
    setStorageKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [handleStorageChange]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.fbq) {
      return;
    }
    if (storageKey === 0 && !consent.adConsent) {
      return;
    }
    window.fbq("consent", consent.adConsent ? "grant" : "revoke");
  }, [consent.adConsent, storageKey]);

  const isProduction = process.env.NEXT_PUBLIC_VERCEL_ENV === "production";

  return (
    <>
      {isProduction && (
        <>
          <Script
            id="gtag-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                window.gtag = gtag;
                gtag('consent', 'default', {
                  ad_storage: '${consentDefault}',
                  ad_user_data: '${consentDefault}',
                  ad_personalization: '${consentDefault}',
                  analytics_storage: '${consentDefault}',
                  url_passthrough: true,
                });
                gtag('js', new Date());
              `,
            }}
          />
          <Script
            src={`/gt/js?id=${GOOGLE_ADS_ID}`}
            strategy="afterInteractive"
          />
        </>
      )}
      {isProduction && consent.adConsent && (
        <>
          <Script id="meta-pixel-base" strategy="afterInteractive">
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
            `}
          </Script>
          <Script id="meta-pixel-init" strategy="afterInteractive">
            {`
              window.fbq && window.fbq('init', '${META_PIXEL_ID}');
              window.fbq && window.fbq('consent', 'grant');
            `}
          </Script>
        </>
      )}
    </>
  );
}
